import { notification } from "antd";
import DeliveriesFilterInfo from "../../context/deliveriesFilterinfo";
import { CommandMessage } from "../../dashboard/CommandTypes";
import Helper from "../../Helper";
import { CommandCenterView } from "../../models/CommandCenterView";
import { ExecutiveCommandView } from "../../models/ExecutiveCommandView";
import TeamInfo from "../../teamInfo";
import { buildFilterInfo } from "../CommandCenter/Helpers/CommandCenterHelper";
import { plotFetch } from "../Helper/ApiRequest";

export const fetchInitialData = async (searchParams: URLSearchParams) => {
    const projectInfo = await fetchSectionData("projectOverview", searchParams);
    const commandView = CommandCenterView.fromApiObject(projectInfo.command_center_view, projectInfo.time_zone);
    const messages = projectInfo.messages.map((msg: any) => {
        return Object.create(msg) as CommandMessage;
    });

    return { project: projectInfo, commandView: commandView, messages: messages };
};

const getEndpoint = (viewType: string): string => {
    switch (viewType) {
        case "projectOverview":
            return "project_info";
        case "weather":
            return "weather";
        case "currentConditions":
            return "current_conditions";
        case "procurementChecklist":
            return "procurement_checklist";
        case "logisticsMap":
            return "logistics_map";
        case "deliveriesMap":
            return "deliveries_map";
        case "deliveriesWeek":
            return "deliveries_week";
        case "deliveriesDay":
            return "deliveries_day";
        case "deliveriesList":
            return "deliveries_list";
        default:
            throw new Error("Unknown view type");
    }
};

export const fetchSectionData = async (viewType: string, searchParams: URLSearchParams, filter?: any): Promise<any> => {
    const apiKey = searchParams.get("key");
    const apiSecret = searchParams.get("secret");
    const viewId = searchParams.get("view");
    if (apiKey && apiSecret) {
        return fetchSectionViewDataWithApi(viewType, apiKey, apiSecret, filter);
    } else if (viewId) {
        return fetchSectionViewDataWithViewId(viewType, viewId);
    }
    return;
};

// Fetch section data using API key and secret is no longer needed since we include data with the initial page
// load. However, we keep this function for backward compatibility.
export const fetchSectionViewDataWithApi = async (viewType: string, key: string, secret: string, filter?: any) => {
    const endpoint = getEndpoint(viewType);
    const url = new URL(Helper.baseUrlAltSubdomain("app") + `/api/dashboard/${endpoint}`);
    if (filter) {
        const filterParams = filter.toApiObject().toString();
        url.search = filterParams;
    }

    return plotFetch(url.toString(), {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${key}:${secret}`,
        },
    })
        .then((res) => {
            if (res.ok) {
                return res.json();
            } else {
                throw new Error(res.statusText || "Failed to fetch project info with API key");
            }
        })
        .catch((error) => {
            notification.error({
                title: "Error",
                description: error.message || "An error occurred while fetching data.",
            });
        });
};

// No longer used. See comment above for parent function
export const fetchSectionViewDataWithViewId = async (
    viewType: string,
    viewId: string,
    filter?: DeliveriesFilterInfo
) => {
    const endpoint = getEndpoint(viewType);
    const url = new URL(Helper.baseUrlAltSubdomain("app") + `/api/dashboard/${endpoint}`);
    if (filter) {
        // oxlint-disable-next-line typescript/no-base-to-string
        const filterParams = filter.toApiObject().toString();
        url.search = filterParams;
    }
    url.searchParams.append("view", viewId);

    const response = await fetch(url.toString(), {
        headers: {
            "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for session auth
    });
    if (response.ok) {
        const data = await response.json();
        return data;
    } else {
        throw new Error(response.statusText || "Failed to fetch project info with user session");
    }
};

export const getCommandCenterViews = (project: any): Promise<any> => {
    const url = `${Helper.baseUrlAltSubdomain(project.claimed_by_team_subdomain)}/projects/${
        project.unique_token
    }/command_center_views`;

    return plotFetch(url).then((res) => res.json());
};

export const updateCommandCenterViews = (project: any, views: CommandCenterView[]): Promise<any> => {
    return Promise.all(
        views.map((view) => {
            const commandCenterView = Object.create(view) as CommandCenterView;
            const method = view.id ? "PUT" : "POST";
            const url = `${Helper.baseUrlAltSubdomain(project.claimed_by_team_subdomain)}/projects/${
                project.unique_token
            }/command_center_views/${method == "PUT" ? view.id : ""}`;
            commandCenterView.commandCenterSections = commandCenterView?.commandCenterSections?.map((section) => {
                section.sectionConfig.map((config: any) => {
                    if (config.filters) {
                        // Ensure filters are in snake_case for the API
                        const filter = buildFilterInfo(config.filters).toApiObject();
                        config.filters = filter;
                    }
                });
                return section;
            });
            const options = {
                method: method,
                body: JSON.stringify({ command_center_view: commandCenterView.toApiObject() }),
            };
            return Helper.plotFetch(url, options, true).then((res) => res.json());
        })
    );
};

export const deleteCommandCenterView = (project: any, viewId: number): Promise<any> => {
    const url = `${Helper.baseUrlAltSubdomain(project.claimed_by_team_subdomain)}/projects/${
        project.unique_token
    }/command_center_views/${viewId}`;
    const options = {
        method: "DELETE",
    };
    return Helper.plotFetch(url, options, true).then((res) => res.json());
};

export const getExecutiveCommandViews = (team: TeamInfo): Promise<any> => {
    const url = `${Helper.baseUrlAltSubdomain(team.subdomain)}/teams/${team.id}/executive_command_views`;

    return Helper.plotFetch(url, {}, true).then((res) => res.json());
};

export const saveExecutiveCommandView = (team: TeamInfo, view: ExecutiveCommandView): Promise<any> => {
    const method = view.id > 0 ? "PUT" : "POST";
    const url = `${Helper.baseUrlAltSubdomain(team.subdomain)}/teams/${team.id}/executive_command_views/${method == "PUT" ? view.id : ""}`;
    const options = {
        method: method,
        body: JSON.stringify({ executive_command_view: view.toApiObject() }),
    };
    return plotFetch(url, options).then((res) => res.json());
};

export const deleteExecutiveCommandView = (team: TeamInfo, viewId: number): Promise<any> => {
    const url = `${Helper.baseUrlAltSubdomain(team.subdomain)}/teams/${team.id}/executive_command_views/${viewId}`;
    const options = {
        method: "DELETE",
    };
    return Helper.plotFetch(url, options, true).then((res) => res);
};

export const generatePublicUrl = (
    subdomain: string,
    projectToken: string,
    commandView: CommandCenterView
): Promise<string> => {
    const url = `${Helper.baseUrlAltSubdomain(subdomain)}/projects/${projectToken}/command_center_views/${commandView.id}/public_url`;
    const options = {
        method: "POST",
    };
    return Helper.plotFetch(url, options, true).then((res) => res.json());
};

export const deletePublicUrl = (
    subdomain: string,
    projectToken: string,
    commandView: CommandCenterView
): Promise<any> => {
    const url = `${Helper.baseUrlAltSubdomain(subdomain)}/projects/${projectToken}/command_center_views/${commandView.id}/public_url`;
    const options = {
        method: "DELETE",
    };
    return Helper.plotFetch(url, options, true).then((res) => {
        return res;
    });
};
